import { ArrowBack } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Header from "../components/layout/Header";
import CustomerInfoCard from "../components/ui/CustomerInfoCard";
import OrderPhotoGallery from "../components/ui/OrderPhotoGallery";
import OrderSummaryCard from "../components/ui/OrderSummaryCard";
import ProductReadyUpload from "../components/ui/ProductReadyUpload";

import { useAppState } from "../contexts/AppStateContext";
import {
  fetchCampusDetails,
  fetchChildrenByCampus,
} from "../services/childDataService";
import {
  getOrder,
  updateOrder,
  updateOrderStatus,
} from "../services/orderService";
import { getAllPackages, getPackage } from "../services/packageService";
import { getPhoto } from "../services/photoService";
import type { Campus, Child } from "../types";
import type { Order, Package, Photo } from "../types/api";

interface LocationState {
  order?: Order;
  package?: Package;
}

const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;
  const { state: appState, dispatch } = useAppState();

  // Data from navigation state (instant load)
  const passedOrder = locationState?.order;
  const passedPackage = locationState?.package;

  // Component state
  const [order, setOrder] = useState<Order | null>(passedOrder || null);
  const [pkg, setPkg] = useState<Package | null>(passedPackage || null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [child, setChild] = useState<Child | null>(null);
  const [campus, setCampus] = useState<Campus | null>(null);

  // Loading states
  const [loadingOrder, setLoadingOrder] = useState<boolean>(!passedOrder);
  const [loadingPackage, setLoadingPackage] = useState<boolean>(!passedPackage);
  const [loadingPhotos, setLoadingPhotos] = useState<boolean>(true);
  const [loadingChild, setLoadingChild] = useState<boolean>(true);
  const [loadingCampus, setLoadingCampus] = useState<boolean>(true);

  const [error, setError] = useState<string>("");

  // Status management state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [newStatus, setNewStatus] = useState<Order["status"] | null>(null);
  const [updating, setUpdating] = useState<boolean>(false);
  const [statusError, setStatusError] = useState<string>("");
  const [statusSuccess, setStatusSuccess] = useState<string>("");

  // Fetch order if not passed
  useEffect(() => {
    const fetchOrderData = async () => {
      if (!orderId) return;

      if (passedOrder) {
        setOrder(passedOrder);
        setLoadingOrder(false);
      } else {
        try {
          setLoadingOrder(true);
          const orderData = await getOrder(orderId);
          setOrder(orderData);
        } catch (err) {
          console.error("Error fetching order:", err);
          setError("Failed to load order. Please try again.");
        } finally {
          setLoadingOrder(false);
        }
      }
    };

    fetchOrderData();
  }, [orderId, passedOrder]);

  // Fetch package if not passed
  useEffect(() => {
    const fetchPackageData = async () => {
      // Stop loading if no packageId
      if (!order?.packageId) {
        setLoadingPackage(false);
        return;
      }

      if (passedPackage) {
        setPkg(passedPackage);
        setLoadingPackage(false);
      } else {
        try {
          setLoadingPackage(true);
          const packageData = await getPackage(order.packageId);
          setPkg(packageData);
        } catch (err) {
          console.error("Error fetching package:", err);
          setError("Failed to load package details.");
        } finally {
          setLoadingPackage(false);
        }
      }
    };

    fetchPackageData();
  }, [order?.packageId, passedPackage]);

  // Fetch packages from AppState if not already loaded
  useEffect(() => {
    const loadPackages = async () => {
      if (appState.packages.length === 0) {
        try {
          const packagesData = await getAllPackages();
          dispatch({ type: "SET_PACKAGES", payload: packagesData });
        } catch (err) {
          console.error("Error loading packages:", err);
        }
      }
    };

    loadPackages();
  }, [appState.packages.length, dispatch]);

  // Fetch photos (always needed)
  useEffect(() => {
    const fetchPhotosData = async () => {
      if (!order?.photoIds || order.photoIds.length === 0) {
        setLoadingPhotos(false);
        return;
      }

      try {
        setLoadingPhotos(true);
        const photoPromises = order.photoIds.map((photoId) =>
          getPhoto(photoId)
        );
        const photosData = await Promise.all(photoPromises);
        setPhotos(photosData);
      } catch (err) {
        console.error("Error fetching photos:", err);
        setError("Failed to load photos.");
      } finally {
        setLoadingPhotos(false);
      }
    };

    fetchPhotosData();
  }, [order?.photoIds]);

  // Fetch child data (always needed)
  useEffect(() => {
    const fetchChildData = async () => {
      if (!order?.childId || !order?.campusId) return;

      try {
        setLoadingChild(true);
        // Fetch all children for the campus and find the matching child
        const children = await fetchChildrenByCampus(order.campusId, true);

        const matchedChild = children.find((c) => c.childID === order.childId);
        if (matchedChild) {
          setChild(matchedChild);
        }
      } catch (err) {
        console.error("Error fetching child:", err);
        // Don't set error here as child data is nice-to-have
      } finally {
        setLoadingChild(false);
      }
    };

    fetchChildData();
  }, [order?.childId, order?.campusId]);

  // Fetch campus data (always needed)
  useEffect(() => {
    const fetchCampusData = async () => {
      if (!order?.campusId) return;

      try {
        setLoadingCampus(true);
        const campusData = await fetchCampusDetails(order.campusId);
        setCampus(campusData);
      } catch (err) {
        console.error("Error fetching campus:", err);
        // Don't set error here as campus data is nice-to-have
      } finally {
        setLoadingCampus(false);
      }
    };

    fetchCampusData();
  }, [order?.campusId]);

  const handlePackageUpdate = async (packageId: string) => {
    if (!order) return;

    try {
      const updatedOrder = await updateOrder(order.id, { packageId });
      setOrder(updatedOrder);

      // Update package display
      const newPackage = appState.packages.find(
        (p) => p.packageId === packageId
      );
      setPkg(newPackage || null);

      setStatusSuccess("Package updated successfully!");
      setTimeout(() => {
        setStatusSuccess("");
      }, 3000);
    } catch (err) {
      console.error("Error updating package:", err);
      setStatusError("Failed to update package. Please try again.");
    }
  };

  const handleUploadComplete = async () => {
    // Refresh photos after upload
    if (!order?.photoIds) return;

    try {
      const photoPromises = order.photoIds.map((photoId) => getPhoto(photoId));
      const photosData = await Promise.all(photoPromises);
      setPhotos(photosData);

      // Check if all photos have a finalUrl and update order status to fulfilled if needed
      const allPhotosHaveFinalUrl = photosData.every(
        (photo) => !!photo.finalUrl
      );

      if (
        allPhotosHaveFinalUrl &&
        (order.status === "paid" || order.status === "processing")
      ) {
        try {
          const updatedOrder = await updateOrderStatus(order.id, "fulfilled");
          setOrder(updatedOrder);
          setStatusSuccess("Order status automatically updated to fulfilled!");
          setTimeout(() => {
            setStatusSuccess("");
          }, 4000);
        } catch (err) {
          console.error("Error updating order status:", err);
          // Don't show error for status update since photos were successfully refreshed
        }
      }
    } catch (err) {
      console.error("Error refreshing photos:", err);
    }
  };

  const handleBackClick = () => {
    navigate("/dashboard");
  };

  // Status management handlers
  const handleStatusChange = (status: Order["status"]) => {
    setNewStatus(status);
    setConfirmDialogOpen(true);
  };

  const handleConfirmAction = async () => {
    if (!order || !newStatus) return;

    setConfirmDialogOpen(false);
    setUpdating(true);
    setStatusError("");
    setStatusSuccess("");

    try {
      const updatedOrder = await updateOrderStatus(order.id, newStatus);

      // Update local state
      setOrder(updatedOrder);
      setStatusSuccess(`Order status updated to ${newStatus} successfully!`);

      // Clear success message after 3 seconds
      setTimeout(() => {
        setStatusSuccess("");
      }, 3000);
    } catch (err) {
      console.error("Error updating order status:", err);
      setStatusError("Failed to update order status. Please try again.");
    }

    setUpdating(false);
    setNewStatus(null);
  };

  const handleCancelAction = () => {
    setConfirmDialogOpen(false);
    setNewStatus(null);
  };

  const allStatuses = [
    "pending",
    "pending_payment",
    "paid",
    "processing",
    "fulfilled",
    "refund_requested",
    "refunded",
    "canceled",
  ] as const;

  // Main loading state (only for critical data)
  const isLoading = loadingOrder || loadingPackage;

  if (isLoading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        <Header />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
            }}
          >
            <CircularProgress size={60} />
          </Box>
        </Container>
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        <Header />
        <Container maxWidth="xl" sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || "Order not found"}
          </Alert>
          <Button variant="outlined" onClick={handleBackClick}>
            Back to Dashboard
          </Button>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Header />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackClick}
            sx={{ mb: 2 }}
          >
            Back to Dashboard
          </Button>

          {/* Status Messages */}
          {statusError && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setStatusError("")}
            >
              {statusError}
            </Alert>
          )}
          {statusSuccess && (
            <Alert
              severity="success"
              sx={{ mb: 2 }}
              onClose={() => setStatusSuccess("")}
            >
              {statusSuccess}
            </Alert>
          )}

          {/* Title and Status Buttons */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Order Details
            </Typography>

            {/* Status Management Actions */}
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              {/* <Button
                variant="outlined"
                color="primary"
                sx={{
                  fontWeight: 600,
                  px: 3,
                }}
              >
                Resend Email to Parent
              </Button> */}

              <FormControl
                size="small"
                sx={{ minWidth: 200 }}
                disabled={updating}
              >
                <InputLabel>Order Status</InputLabel>
                <Select
                  value={order?.status || ""}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  label="Order Status"
                  displayEmpty
                >
                  {allStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status.charAt(0).toUpperCase() +
                        status.slice(1).replace(/_/g, " ")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <Typography variant="body1" color="text.secondary">
            View order information, photos, and upload product-ready files
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid size={{ xs: 12, lg: 8 }}>
            {/* Order Summary */}
            <Box sx={{ mb: 3 }}>
              <OrderSummaryCard
                order={order}
                package={pkg}
                packages={appState.packages}
                onPackageUpdate={handlePackageUpdate}
              />
            </Box>

            {/* Photo Gallery */}
            <Box sx={{ mb: 3 }}>
              <OrderPhotoGallery
                photos={photos}
                loading={loadingPhotos}
                {...(order?.transaction?.note && {
                  note: order.transaction.note,
                })}
              />
            </Box>
          </Grid>

          {/* Right Column */}
          <Grid size={{ xs: 12, lg: 4 }}>
            {/* Customer Info */}
            <Box sx={{ mb: 3 }}>
              <CustomerInfoCard
                order={order}
                child={child}
                campus={campus}
                loading={loadingChild || loadingCampus}
              />
            </Box>

            {/* Product Ready Upload */}
            <Box sx={{ mb: 3 }}>
              <ProductReadyUpload
                photos={photos}
                campusId={order.campusId}
                childId={order.childId}
                onUploadComplete={handleUploadComplete}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelAction}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          Confirm Status Change
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            Change order status from{" "}
            <strong>
              {order?.status
                ? order.status.charAt(0).toUpperCase() +
                  order.status.slice(1).replace(/_/g, " ")
                : "unknown"}
            </strong>{" "}
            to{" "}
            <strong>
              {newStatus
                ? newStatus.charAt(0).toUpperCase() +
                  newStatus.slice(1).replace(/_/g, " ")
                : "unknown"}
            </strong>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={
              newStatus === "fulfilled" || newStatus === "refunded"
                ? newStatus === "fulfilled"
                  ? "success"
                  : "error"
                : "primary"
            }
            autoFocus
          >
            Confirm Change
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderDetail;
